import {type ReactElement, type ReactNode, useState} from 'react';
import {DistributionBlock} from './DistributionBlock';
import {KpiBlock} from './KpiBlock';
import {MatrixBlock} from './MatrixBlock';
import {SeriesChartBlock} from './SeriesChartBlock';
import {TableBlock} from './TableBlock';
import {ViewSwitcher, type ViewName} from './ViewSwitcher';
import {
  type Report,
  type ReportBlock,
  type ReportBlockType,
  type ReportTableBlock
} from '@/api/reporting';

type BlockOf<T extends ReportBlockType> = Extract<ReportBlock, {type: T}>;
// ReactElement rather than ReactNode: React 19's ReactNode admits a Promise, which would make every
// callback below look like an un-awaited async function to the linter.
type BlockRenderer<T extends ReportBlockType> = (props: {
  readonly block: BlockOf<T>;
  readonly actions?: ReactNode;
}) => ReactElement;

// One renderer per block type the contract names. ReportRenderer.test reads the contract's
// `block_type` enum and fails when a type has no entry here, which is what makes a new block type a
// frontend change that cannot be merged half-done. Data never touches this file.
export const BLOCK_RENDERERS: {readonly [T in ReportBlockType]: BlockRenderer<T>} = {
  kpi: KpiBlock,
  line_chart: SeriesChartBlock,
  bar_chart: SeriesChartBlock,
  scatter_chart: SeriesChartBlock,
  pie_chart: DistributionBlock,
  linear_bar_chart: DistributionBlock,
  table: TableBlock,
  matrix: MatrixBlock
};

type ViewableBlock = Exclude<ReportBlock, {type: 'kpi' | 'table' | 'matrix'}>;

// The table view of a chart is its own data laid out in columns, so the contract needs no second
// column list.
function tableView(block: ViewableBlock): ReportTableBlock {
  const columns: ReportTableBlock['columns'] =
    'series' in block
      ? [
          block.x,
          ...block.series.map((series) => ({
            ...series,
            align: 'right' as const,
            format: 'decimal' as const
          }))
        ]
      : [block.category, {...block.value, align: 'right', format: block.format ?? 'integer'}];

  return {
    id: block.id,
    type: 'table',
    title: block.title,
    // An absent description must stay absent (exactOptionalPropertyTypes), not become undefined.
    ...(block.description === undefined ? {} : {description: block.description}),
    columns,
    rows: block.rows
  };
}

// A block arrives in its default view (`type`); when it lists `views`, the reader can switch among
// them. The choice is local and per block, and the rows are the ones already fetched.
function ViewedBlock({block}: {readonly block: ViewableBlock}) {
  const [view, setView] = useState<ViewName>(block.type);
  const {views} = block;
  const actions =
    views && views.length > 1 ? (
      <ViewSwitcher views={views} value={view} onChange={setView} />
    ) : undefined;

  if (view === 'table') {
    return <TableBlock block={tableView(block)} actions={actions} />;
  }

  const Renderer = BLOCK_RENDERERS[view] as BlockRenderer<ReportBlockType>;
  return <Renderer block={{...block, type: view} as ReportBlock} actions={actions} />;
}

function renderBlock(block: ReportBlock): ReactElement {
  if (block.type === 'kpi' || block.type === 'table' || block.type === 'matrix') {
    const Renderer = BLOCK_RENDERERS[block.type] as BlockRenderer<ReportBlockType>;
    return <Renderer key={block.id} block={block} />;
  }

  return <ViewedBlock key={block.id} block={block} />;
}

// Consecutive KPI tiles share one grid row, as the dashboard's do, and consecutive half-width
// blocks pair up two to a row; every other block takes the full width in the order Data wrote them.
type BlockGroup = [ReportBlock, ...ReportBlock[]];
type GroupKind = 'kpi' | 'half' | 'full';

function groupKind(block: ReportBlock): GroupKind {
  if (block.type === 'kpi') {
    return 'kpi';
  }

  return block.width === 'half' ? 'half' : 'full';
}

function groupBlocks(blocks: readonly ReportBlock[]): BlockGroup[] {
  const groups: BlockGroup[] = [];
  for (const block of blocks) {
    const current = groups.at(-1);
    const kind = groupKind(block);
    if (kind !== 'full' && current !== undefined && groupKind(current[0]) === kind) {
      current.push(block);
    } else {
      groups.push([block]);
    }
  }

  return groups;
}

const GROUP_GRID: Record<Exclude<GroupKind, 'full'>, string> = {
  kpi: 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4',
  half: 'grid grid-cols-1 gap-6 xl:grid-cols-2'
};

export function ReportRenderer({report}: {readonly report: Report}) {
  return (
    <div className="space-y-6">
      {groupBlocks(report.blocks).map((group) => {
        const kind = groupKind(group[0]);
        return kind === 'full' ? (
          renderBlock(group[0])
        ) : (
          <div key={group[0].id} className={GROUP_GRID[kind]}>
            {group.map((block) => renderBlock(block))}
          </div>
        );
      })}
    </div>
  );
}
